import { Flex, Stack, useMantineTheme } from '@mantine/core';
import type { Row } from '@tanstack/react-table';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { FormSummary, FormSummaryItem, Text, Well } from '@feathr/components';
import { FailureReasonLine, hasRunFinished } from '@feathr/extender/components/ImportRunLines';
import { moment, TimeFormat } from '@feathr/hooks';

import DownloadErrorsLine from './DownloadErrorsLine';
import { getRunSummaryItems, hasRunDetails, hasRunStarted } from './ImportRunDetails.utils';
import ImportTagsLine from './ImportTagsLine';
import InitiatedByLine from './InitiatedByLine';
import RejectionBreakdown from './RejectionBreakdown';
import SecondRecordsLine from './SecondRecordsLine';

import * as styles from './ImportRunDetails.css';

interface IImportRunDetailsProps {
  importer: Importer;
}

/**
 * The Import details panel under an expanded imports-table row. Its Well sets three columns side by
 * side: the run's labeled values, the reasons rows were refused, and the import's actions. Each item
 * decides for itself whether it applies. The start time is in the browser's timezone, as in the
 * table's date columns.
 */
function ImportRunDetails({ importer }: Readonly<IImportRunDetailsProps>): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  if (!hasRunDetails(importer)) {
    return null;
  }

  // A draft, a queued import, or one that failed before it started has no Initiated items.
  const started = hasRunStarted(importer)
    ? moment.utc(importer.get('date_started'), moment.ISO_8601).tz(moment.tz.guess())
    : undefined;
  const hasErrorFile = hasRunFinished(importer) && !!importer.get('errors_key');

  return (
    <Stack data-name={'import-run-details'} gap={theme.spacing['2']}>
      {/* Placeholder copy, pending Product and Design (decision 23). */}
      <Text.DefaultSemibold>{t('Import details')}</Text.DefaultSemibold>
      <Well
        className={styles.well}
        layout={'vertical'}
        name={'import-run-details-items'}
        theme={'white'}
      >
        <Flex align={'flex-start'} gap={theme.spacing['6']}>
          <FormSummary className={classNames(styles.summary, styles.fields)} fitLabels={true}>
            {started ? (
              <InitiatedByLine
                date={started.format(TimeFormat.shortDate)}
                time={started.format(TimeFormat.timeAndTimeZone)}
                userId={importer.get('created_by')}
              />
            ) : undefined}
            {importer.get('state') === 'failed' ? (
              <FormSummaryItem
                label={t('Failure reason')}
                value={<FailureReasonLine importer={importer} />}
              />
            ) : undefined}
            <>
              {getRunSummaryItems(importer, t).map(({ label, value }) => (
                <FormSummaryItem key={label} label={label} value={value} />
              ))}
            </>
            <SecondRecordsLine importer={importer} />
            <ImportTagsLine importer={importer} />
          </FormSummary>
          <RejectionBreakdown
            className={classNames(styles.summary, styles.problems)}
            importer={importer}
          />
          {hasErrorFile ? (
            <Stack align={'flex-start'} className={styles.actions} gap={theme.spacing['2']}>
              <DownloadErrorsLine href={importer.downloadErrorUrl} />
            </Stack>
          ) : undefined}
        </Flex>
      </Well>
    </Stack>
  );
}

const ObservedImportRunDetails = observer(ImportRunDetails);

export default ObservedImportRunDetails;

/**
 * Adapts the panel to `TanStackTable`'s `renderSubComponent`, which hands over the row rather
 * than the model. Module scope keeps its identity stable across renders.
 */
export function renderImportRunDetails(row: Row<Importer>): JSX.Element {
  return <ObservedImportRunDetails importer={row.original} />;
}
